import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff, ArrowLeft } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'
import { cn } from '@/lib/utils'
import { getPasswordStrength } from '@/lib/password-strength'

interface PasswordDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  step: 'password' | 'otp'
  currentPassword: string
  newPassword: string
  confirmPassword: string
  otpCode: string
  onCurrentPasswordChange: (value: string) => void
  onNewPasswordChange: (value: string) => void
  onConfirmPasswordChange: (value: string) => void
  onOtpChange: (value: string) => void
  onSendCode: () => Promise<void>
  onVerifyCode: () => void
  onResendCode: () => void
  isSendingCode: boolean
  isVerifying: boolean
  isResending: boolean
}

function PasswordInput({
  id,
  label,
  value,
  onChange,
  placeholder,
  autoComplete,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  autoComplete?: string
}) {
  const { t } = useTranslation()
  const [show, setShow] = useState(false)

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium" htmlFor={id}>
        {label}
      </label>
      <div className="relative">
        <Input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className="pr-10"
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute right-1 top-1/2 size-7 -translate-y-1/2 p-0 text-muted-foreground hover:text-foreground"
          onClick={() => setShow((v) => !v)}
          aria-label={show ? t('settings.dialogs.password.hidePassword') : t('settings.dialogs.password.showPassword')}
        >
          {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </Button>
      </div>
    </div>
  )
}

export function PasswordDialog({
  open,
  onOpenChange,
  step,
  currentPassword,
  newPassword,
  confirmPassword,
  otpCode,
  onCurrentPasswordChange,
  onNewPasswordChange,
  onConfirmPasswordChange,
  onOtpChange,
  onSendCode,
  onVerifyCode,
  onResendCode,
  isSendingCode,
  isVerifying,
  isResending,
}: PasswordDialogProps) {
  const { t } = useTranslation()
  const strength = getPasswordStrength(newPassword)
  const mismatch = confirmPassword.length > 0 && confirmPassword !== newPassword
  const canSubmitStep1 =
    currentPassword.length > 0 && newPassword.length >= 8 && confirmPassword === newPassword

  const handleSend = async () => {
    try {
      await onSendCode()
    } catch {
      // Error toast is already shown by the parent mutation's onError
    }
  }

  const handleBack = () => {
    onOtpChange('')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {step === 'password'
              ? t('settings.dialogs.password.title')
              : t('settings.dialogs.password.step2Title', 'Verify password change')}
          </DialogTitle>
          <DialogDescription>
            {step === 'password'
              ? t('settings.dialogs.password.description')
              : t('settings.dialogs.password.step2Description', 'Enter the 6-digit code we sent to your email to confirm the password change.')}
          </DialogDescription>
        </DialogHeader>

        {step === 'password' ? (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (canSubmitStep1) void handleSend()
            }}
          >
            <div className="flex flex-col gap-4 py-4">
              <PasswordInput
                id="settings-current-password"
                label={t('settings.dialogs.password.currentPassword')}
                value={currentPassword}
                onChange={onCurrentPasswordChange}
                autoComplete="current-password"
              />
              <div className="flex flex-col gap-2">
                <PasswordInput
                  id="settings-new-password"
                  label={t('settings.dialogs.password.newPassword')}
                  value={newPassword}
                  onChange={onNewPasswordChange}
                  placeholder={t('auth.invitation.passwordPlaceholder')}
                  autoComplete="new-password"
                />
                {newPassword.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                        <div
                          className={cn('h-full transition-all duration-300', strength.color)}
                          style={{ width: `${(strength.score / 5) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {t(`settings.dialogs.password.strength.${strength.label}` as const)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">{t('settings.dialogs.password.strengthLabel')}</p>
                  </div>
                )}
              </div>
              <PasswordInput
                id="settings-confirm-password"
                label={t('settings.dialogs.password.confirmPassword')}
                value={confirmPassword}
                onChange={onConfirmPasswordChange}
                autoComplete="new-password"
              />
              {mismatch && (
                <p className="text-xs text-destructive">{t('auth.errors.passwordMismatch')}</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('settings.dialogs.password.cancel')}
              </Button>
              <Button type="submit" disabled={isSendingCode || !canSubmitStep1}>
                {isSendingCode
                  ? t('settings.dialogs.password.sendingCode', 'Sending code...')
                  : t('settings.dialogs.password.sendCode', 'Send verification code')}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="flex flex-col gap-5 py-2">
            <div className="rounded-lg bg-muted/30 p-4">
              <p className="text-sm text-muted-foreground">
                {t('settings.dialogs.password.verificationCodeSent', 'A 6-digit code has been sent to your email address.')}
              </p>
            </div>

            <div className="flex flex-col items-center gap-4">
              <label className="text-sm font-medium">
                {t('settings.dialogs.password.codeLabel', '6-digit code')}
              </label>
              <InputOTP
                maxLength={6}
                value={otpCode}
                onChange={onOtpChange}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>

              <Button
                type="button"
                variant="link"
                size="sm"
                disabled={isResending}
                onClick={onResendCode}
                className="h-auto py-0 text-muted-foreground hover:text-foreground"
              >
                {isResending
                  ? t('settings.dialogs.password.resending', 'Resending...')
                  : t('settings.dialogs.password.resendCode', 'Resend code')}
              </Button>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleBack}>
                <ArrowLeft className="mr-1 size-4" />
                {t('settings.dialogs.password.back', 'Back')}
              </Button>
              <Button
                type="button"
                disabled={isVerifying || otpCode.length !== 6}
                onClick={onVerifyCode}
              >
                {isVerifying
                  ? t('settings.dialogs.password.verifying', 'Verifying...')
                  : t('settings.dialogs.password.verifyAndSave', 'Verify & save')}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
